'use client';

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { ThemeProvider as MUIThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { createAppTheme, type ThemeMode } from '@/styles/themes/theme';

interface ThemeContextType {
  mode: ThemeMode;
  toggleMode: () => void;
  setMode: (mode: ThemeMode) => void;
  systemPreference: ThemeMode;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    // Return default values for SSR/static generation
    return {
      mode: 'light' as ThemeMode,
      toggleMode: () => {},
      setMode: () => {},
      systemPreference: 'light' as ThemeMode,
    };
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

/**
 * Enhanced ThemeProvider with Stripe-inspired theme switching
 * - Smooth transitions between light/dark modes
 * - Persistent theme preference
 * - System preference detection
 * - CSS class-based theme switching for better performance
 */
export function ThemeProvider({ children }: ThemeProviderProps) {
  const [mode, setModeState] = useState<ThemeMode>('light');
  const [systemPreference, setSystemPreference] = useState<ThemeMode>('light');
  const [mounted, setMounted] = useState(false);
  const hasManualPreferenceRef = useRef(false);

  // Apply theme class to HTML element for CSS variable switching
  const applyThemeClass = useCallback((newMode: ThemeMode) => {
    const html = document.documentElement;

    // Remove existing theme classes
    html.classList.remove('theme-light', 'theme-dark');

    // Add new theme class
    html.classList.add(`theme-${newMode}`);

    // Set data attribute for CSS selectors
    html.setAttribute('data-theme', newMode);

    // Update meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute(
        'content',
        newMode === 'light' ? '#ffffff' : '#0a2540' // Stripe colors
      );
    } else {
      const meta = document.createElement('meta');
      meta.name = 'theme-color';
      meta.content = newMode === 'light' ? '#ffffff' : '#0a2540';
      document.head.appendChild(meta);
    }
  }, []);

  // Initialize theme from localStorage or system preference
  useEffect(() => {
    setMounted(true);

    const savedMode = localStorage.getItem('theme-mode') as ThemeMode | null;

    if (savedMode && (savedMode === 'light' || savedMode === 'dark')) {
      hasManualPreferenceRef.current = true;
      setModeState(savedMode);
      applyThemeClass(savedMode);
    } else {
      hasManualPreferenceRef.current = false;
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      const initialMode: ThemeMode = prefersDark ? 'dark' : 'light';
      setModeState(initialMode);
      applyThemeClass(initialMode);
    }
  }, [applyThemeClass]);

  // Detect system color scheme preference and follow it when no manual override exists
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (event: MediaQueryListEvent | MediaQueryList) => {
      const nextSystemMode: ThemeMode = event.matches ? 'dark' : 'light';
      setSystemPreference(nextSystemMode);

      if (!hasManualPreferenceRef.current) {
        setModeState(nextSystemMode);
        applyThemeClass(nextSystemMode);
        window.dispatchEvent(new CustomEvent('theme-change', { detail: { mode: nextSystemMode, source: 'system' } }));
      }
    };

    // Initial check
    handleChange(mediaQuery);

    // Listen for changes (with legacy fallback)
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, [applyThemeClass]);

  // Set mode with persistence and smooth transition
  const setMode = useCallback((newMode: ThemeMode) => {
    hasManualPreferenceRef.current = true;
    setModeState(newMode);
    localStorage.setItem('theme-mode', newMode);
    applyThemeClass(newMode);

    // Dispatch custom event for other components to listen to
    window.dispatchEvent(new CustomEvent('theme-change', { detail: { mode: newMode, source: 'manual' } }));
  }, [applyThemeClass]);

  // Toggle between light and dark modes
  const toggleMode = useCallback(() => {
    const newMode = mode === 'light' ? 'dark' : 'light';
    setMode(newMode);
  }, [mode, setMode]);

  // Create MUI theme based on current mode
  const theme = createAppTheme(mounted ? mode : 'light');

  // Prevent flash of unstyled content
  if (!mounted) {
    return (
      <MUIThemeProvider theme={theme}>
        <CssBaseline />
        <div style={{ visibility: 'hidden' }}>{children}</div>
      </MUIThemeProvider>
    );
  }

  return (
    <ThemeContext.Provider value={{ mode, toggleMode, setMode, systemPreference }}>
      <MUIThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MUIThemeProvider>
    </ThemeContext.Provider>
  );
}

/**
 * Hook to get current theme mode without access to toggle functions
 */
export const useThemeMode = () => {
  const { mode } = useTheme();
  return mode;
};

/**
 * Hook to get system color scheme preference
 */
export const useSystemTheme = () => {
  const { systemPreference } = useTheme();
  return systemPreference;
};
